"use client";
import React, { useState, useMemo, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { getAuthorArticlesAPI } from '@/services/userService';

const ITEMS_PER_PAGE = 5;

function getVisiblePages(current, last, width = 5) {
    if (last <= width) {
        return Array.from({ length: last }, (_, i) => i + 1);
    }
    const half = Math.floor(width / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(last, start + width - 1);
    if (end - start < width - 1) {
        start = Math.max(1, end - width + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

const MoreSectionAuthor = ({ authorDetails, pagination, authorSlug }) => {
    const [articles, setArticles] = useState(Array.isArray(authorDetails) ? authorDetails : []);
    const [blogPagination, setBlogPagination] = useState(pagination || {});
    const [currentPage, setCurrentPage] = useState(pagination?.current_page || 1);
    const totalPages = blogPagination?.last_page || 1;

    const visiblePages = useMemo(
        () => getVisiblePages(currentPage, totalPages, 5),
        [currentPage, totalPages]
    );

    const goToPage = useCallback(async (page) => {
        if (page < 1 || page > totalPages) return;
        const response = await getAuthorArticlesAPI({
            id: authorSlug,
            page,
            per_page: ITEMS_PER_PAGE,
        });
        if (response?.data) {
            setArticles(response.data.articles || []);
            setBlogPagination(response.pagination || {});
            setCurrentPage(page);
            const sectionEl = document.querySelector('.author-articles-section-ap');
            if (sectionEl) {
                sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [authorSlug, totalPages]);

    return (
        <>
            <div className="articles-list-ap">
                {articles.map((article, index) => (
                    <div className="article-card-ap" key={index}>
                        <div className="article-content-ap">
                            <h3 className="article-title-ap">
                                <Link href={`/article/${article.slug}`} target='_blank' style={{ textDecoration: 'none', color: '#0C0C0C', cursor: 'pointer' }}>
                                    {article.title}
                                </Link>
                            </h3>
                            <p className="article-description-ap">
                                {article.excerpt}
                            </p>
                            <Link href={`/article/${article.slug}`} className="article-link-ap">
                                Read More
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Link>
                        </div>
                        {article?.image?.url && (
                            <div className="article-image-ap">
                                <Image
                                    src={article.image.url}
                                    alt={article.title}
                                    width={400}
                                    height={250}
                                    className="article-img-ap"
                                />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {totalPages > 1 && (
                <div className="pagination-container-ap">
                    <button
                        type="button"
                        className="pag-icon-btn-ap"
                        onClick={() => goToPage(1)}
                        disabled={currentPage <= 1}
                        aria-label="First page"
                    >
                        <ChevronsLeft size={20} strokeWidth={1.75} />
                    </button>
                    <button
                        type="button"
                        className="pag-box-btn-ap pag-box-btn-ap--prev"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                        aria-label="Previous page"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    {visiblePages.map((page) => (
                        <button
                            key={page}
                            type="button"
                            className={`pag-num-ap ${currentPage === page ? 'is-active' : ''}`}
                            onClick={() => goToPage(page)}
                            aria-label={`Page ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    ))}

                    <button
                        type="button"
                        className="pag-box-btn-ap pag-box-btn-ap--next"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        aria-label="Next page"
                    >
                        <ChevronRight size={18} />
                    </button>
                    <button
                        type="button"
                        className="pag-icon-btn-ap pag-icon-btn-ap--emphasis"
                        onClick={() => goToPage(totalPages)}
                        disabled={currentPage >= totalPages}
                        aria-label="Last page"
                    >
                        <ChevronsRight size={20} strokeWidth={1.75} />
                    </button>
                </div>
            )}
        </>
    );
};

export default MoreSectionAuthor;
